import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Task } from '../../lib/db';
import { Trash2 } from 'lucide-react';
import TaskDependencySelector from './TaskDependencySelector';

interface TaskFormProps {
  task?: Task;
  onSubmit: (data: Partial<Task>) => Promise<void>;
  onCancel: () => void;
  onDelete?: (taskId: string) => Promise<void>;
  availableTasks: Task[];
  showFrequency?: boolean;
}

function TaskForm({ task, onSubmit, onCancel, onDelete, availableTasks, showFrequency }: TaskFormProps) {
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: task ? {
      ...task,
      start_date: task.start_date ? new Date(task.start_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      deadline: task.deadline ? new Date(task.deadline).toISOString().split('T')[0] : '',
      end_date: task.end_date ? new Date(task.end_date).toISOString().split('T')[0] : '',
      dependencies: task.dependencies || []
    } : {
      title: '',
      description: '',
      priority: 'Medium',
      status: 'To Do',
      task_type: 'To Do',
      frequency: 'Daily',
      recurrence_day: '1', // Default to Monday
      start_date: new Date().toISOString().split('T')[0],
      deadline: '',
      end_date: '',
      dependencies: []
    },
  });

  const priorities = ['Low', 'Medium', 'High', 'Critical'];
  const statuses = [
    'To Do',
    'Waiting',
    'Delegated',
    'In Progress',
    'Stuck',
    'Failed',
    'Superseded',
  ];
  const taskTypes = ['To Do', 'Waiting', 'Chasing', 'In Progress', 'Done', 'Parked'];
  const frequencies = ['Daily', 'Weekdays', 'Weekly', 'Monthly', 'Quarterly', 'Annual'];
  const weekdays = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  // Watch the frequency field to conditionally show weekday selector
  const watchFrequency = watch('frequency');

  // State for dependency management
  const [selectedDependencies, setSelectedDependencies] = useState<string[]>(task?.depends_on || []);
  const [dependencyType, setDependencyType] = useState<'none' | 'sequential' | 'parallel'>(
    task?.dependency_type || 'none'
  );

  // Filter out the current task from available dependencies to prevent circular dependencies

  const handleFormSubmit = async (data: {
    title: string;
    description: string;
    sop?: string;
    priority: string;
    status: string;
    task_type: string;
    frequency?: string;
    recurrence_day?: string;
    start_date: string;
    deadline: string;
    end_date: string;
    dependencies: string[];
    depends_on: string[];
    dependency_type: 'none' | 'sequential' | 'parallel';
    dependency_group: string;
  }) => {
    try {
      // Clean up the data before submitting
      const cleanData: Partial<Task> = {
        // Only include task fields if editing existing task
        ...(task ? { ...task } : {}),
        // Update form fields
        title: data.title,
        description: data.description,
        sop: data.sop || undefined,
        priority: data.priority,
        status: data.status,
        task_type: data.task_type as Task['task_type'],
        frequency: data.frequency as Task['frequency'],
        recurrence_day: data.recurrence_day ? parseInt(data.recurrence_day) : undefined,
        start_date: data.start_date || new Date().toISOString(),
        deadline: data.deadline || null,
        end_date: data.end_date || null,
        dependencies: data.dependencies || [],
        depends_on: selectedDependencies,
        dependency_type: dependencyType,
        dependency_group: data.dependency_group || undefined
      };

      await onSubmit(cleanData);
    } catch (error) {
      console.error('Error in handleFormSubmit:', error);
      throw error; // Re-throw to let parent handle
    }
  };

  const handleDelete = async () => {
    if (!task || !onDelete) return;
    
    if (confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      await onDelete(task.id);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          {task ? 'Edit Task' : 'New Task'}
        </h3>
      </div>

      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
          Title
        </label>
        <input
          type="text"
          id="title"
          {...register('title', { required: 'Title is required' })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          id="description"
          rows={3}
          {...register('description')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
      </div>
      
      {showFrequency && (
        <div>
          <label htmlFor="sop" className="block text-sm font-medium text-gray-700">
            Standard Operating Procedure (SOP)
            <span className="text-xs text-gray-500 ml-1">(Step-by-step instructions)</span>
          </label>
          <textarea
            id="sop"
            rows={4}
            {...register('sop')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="1. First step...&#10;2. Second step...&#10;3. Third step..."
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
            Priority
          </label>
          <select
            id="priority"
            {...register('priority')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            {priorities.map((priority) => (
              <option key={priority} value={priority}>
                {priority}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            Status
          </label>
          <select
            id="status"
            {...register('status')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="task_type" className="block text-sm font-medium text-gray-700">
            Task Type
          </label>
          <select
            id="task_type"
            {...register('task_type')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            {taskTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {showFrequency && (
          <div>
            <label htmlFor="frequency" className="block text-sm font-medium text-gray-700">
              Frequency
            </label>
            <select
              id="frequency"
              {...register('frequency')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              {frequencies.map((freq) => (
                <option key={freq} value={freq}>
                  {freq}
                </option>
              ))}
            </select>
          </div>
        )}

        {showFrequency && watchFrequency === 'Weekly' && (
          <div>
            <label htmlFor="recurrence_day" className="block text-sm font-medium text-gray-700">
              Repeat on
            </label>
            <select
              id="recurrence_day"
              {...register('recurrence_day')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              {weekdays.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
            Start Date
          </label>
          <input
            type="date"
            id="start_date"
            {...register('start_date', { required: 'Start date is required' })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
          {errors.start_date && (
            <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="deadline" className="block text-sm font-medium text-gray-700">
            Deadline <span className="text-sm text-gray-500">(optional)</span>
          </label>
          <input
            type="date"
            id="deadline"
            {...register('deadline')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
            End Date <span className="text-sm text-gray-500">(optional)</span>
          </label>
          <input
            type="date"
            id="end_date"
            {...register('end_date')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Notes field */}
      <div className="mt-4">
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
          Notes <span className="text-sm text-gray-500">(optional)</span>
        </label>
        <textarea
          id="notes"
          {...register('notes')}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          placeholder="Add any additional notes about this task..."
        />
      </div>


      {/* Task Dependencies */}
      <div className="border-t pt-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Task Dependencies</h3>
        <TaskDependencySelector
          currentTaskId={task?.id}
          selectedDependencies={selectedDependencies}
          availableTasks={availableTasks}
          dependencyType={dependencyType}
          onDependenciesChange={setSelectedDependencies}
          onDependencyTypeChange={setDependencyType}
        />
      </div>

      <div className="flex justify-between items-center">
        {task && onDelete && (
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            Delete Task
          </button>
        )}
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            {task ? 'Update Task' : 'Create Task'}
          </button>
        </div>
      </div>
    </form>
  );
}

export default TaskForm;